<template>
  <div class="w-full h-full relative overflow-auto">
    <!-- MAP SECTION -->
    <div class="fixed w-full h-full">
      <Map ref="mapRef" @ready="traceRouteWithDelay" />
    </div>

    <!-- HEADER BUTTON BACK -->
    <div class="fixed top-4 w-full z-10">
      <div class="mobile-status-padding"></div>
      <button
        @click="closeDialog"
        class="relative top-0 left-4 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"
      >
        <svg
          class="w-5 h-5 text-slate-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
    </div>

    <!-- OVERLAY INFO LIVRAISON -->
    <div class="mt-[70vh] w-full pointer-events-none relative p-5">
      <div
        class="rounded-[3rem] bg-gradient-to-r from-primary to-primary-600 shadow-2xl !pointer-events-auto overflow-hidden"
      >
        <!-- INFOS DU COLIS -->
        <div class="p-8 pb-0 flex gap-3 items-center">
          <div
            class="w-20 h-20 bg-white/20 overflow-hidden rounded-3xl flex items-center justify-center"
          >
            <ImgComponent
              v-if="packageCategory === 'document'"
              src="/images/packageDelivery/document.png"
              class="w-full h-full object-contain"
              alt="Document"
            />
            <ImgComponent
              v-else
              src="/images/packageDelivery/package.png"
              class="w-full h-full object-contain"
              alt="Colis"
            />
          </div>

          <div>
            <div class="text-white flex-1">
              <div class="text-2xl font-bold">
                {{
                  deliveryData?.title ||
                  (packageCategory === 'document' ? 'Document' : 'Colis')
                }}
              </div>
              <div class="flex gap-2 items-center">
                <div class="text-sm opacity-90">
                  #{{
                    deliveryData?.reference ||
                    deliveryData?.trackingCode ||
                    deliveryData?.deliveryCode ||
                    deliveryData?.id ||
                    'N/A'
                  }}
                </div>
                <div>&bull;</div>
                <div class="mt-1 text-sm opacity-80">
                  {{ pickupMethodDisplay }}
                </div>
              </div>

              <!-- QUICK ACTIONS -->
              <div class="flex gap-2 overflow-x-auto no-scrollbar">
                <button
                  v-if="
                    deliveryData?.status !== 'delivered' &&
                    deliveryData?.status !== 'cancelled' &&
                    deliveryData?.payment?.status !== 'completed' &&
                    deliveryData?.payment?.method !== 'cash' &&
                    deliveryData?.metadata?.paymentLinkEnabled
                  "
                  @click.stop="copyPaymentLink"
                  class="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/80 hover:bg-orange-600 rounded-full text-[10px] font-bold uppercase transition-colors pulse-subtle"
                  title="Copier le lien de paiement"
                >
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  Paiement
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="text-start text-white px-8">
          <div class="text-2xl font-bold">
            {{
              formatPrice(
                deliveryData?.pricing?.total ||
                  deliveryData?._raw?.pricing?.total ||
                  deliveryData?.price ||
                  0
              )
            }}
          </div>
          <div class="text-xs opacity-80">
            {{
              deliveryData?.payment?.status === 'completed'
                ? 'Payé'
                : 'Total livraison'
            }}
          </div>
        </div>

        <!-- SUIVI DE LIVRAISON -->
        <div
          ref="detailContainer"
          class="mt-4 rounded-t-[3rem] p-6 bg-white overflow-auto"
        >
          <!-- Statut et timeline -->
          <div class="flex flex-col gap-3 pb-5">
            <div class="text-xl font-bold title-color">
              Statut :
              <span class="text-primary">{{
                deliveryData?.currentStatusLabel || statusDisplay
              }}</span>
            </div>

            <div
              v-if="deliveryData?.latestTimelineDescription"
              class="text-sm text-primary font-medium italic"
            >
              {{ deliveryData.latestTimelineDescription }}
            </div>

            <!-- Progress bar -->
            <div
              class="relative rounded-full bg-gray-200 h-3 flex items-center"
            >
              <div
                class="absolute left-0 h-full rounded-full bg-gradient-to-r from-primary to-primary-600 transition-all duration-500"
                :style="{
                  width:
                    deliveryData?.progressPercentage !== undefined
                      ? deliveryData.progressPercentage + '%'
                      : progressWidth,
                }"
              ></div>
              <div
                class="absolute h-full flex items-center transition-all duration-500"
                :style="{
                  left:
                    deliveryData?.progressPercentage !== undefined
                      ? deliveryData.progressPercentage + '%'
                      : progressWidth,
                }"
              >
                <div
                  class="w-10 h-10 rounded-full bg-primary shadow-lg flex items-center justify-center -ml-4"
                >
                  <ColorImg
                    src="/images/icons/delivery-bike.png"
                    class="w-6 h-6 object-cover bg-black/60"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Affichage de l'ETA -->
          <div
            v-if="
              ['in_transit', 'inTransit'].includes(deliveryData?.status) &&
              etaMessage
            "
            class="mt-4 w-full flex flex-col items-center justify-center p-4 bg-primary/10 rounded-2xl border-2 border-primary border-dashed animate-pulse"
          >
            <div
              class="text-primary font-bold text-center uppercase tracking-wider mb-1"
            >
              {{ etaMessage.title }}
            </div>
            <div class="text-primary-600 text-xs text-center font-medium px-4">
              {{ etaMessage.subtitle }}
            </div>
          </div>

          <!-- Rider Card -->
          <div
            class="mb-6 p-4 rounded-2xl bg-slate-50 border-slate-100 relative overflow-hidden"
          >
            <div
              class="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 blur-2xl"
            ></div>
            <div class="flex items-center gap-4 relative">
              <div
                class="w-14 h-14 rounded-xl overflow-hidden bg-white border border-slate-200 shrink-0 shadow-sm"
              >
                <ImgComponent
                  :src="
                    deliveryData?.rider?.profilePicture ||
                    deliveryData?.delivery?.rider?.profilePicture ||
                    '/images/rider-placeholder.png'
                  "
                  class="w-full h-full object-cover"
                  onerror="this.src = '/images/rider-placeholder.png'"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div
                  class="text-[10px] text-primary font-bold uppercase tracking-widest mb-0.5"
                >
                  Votre Livreur
                </div>
                <div class="text-base font-bold title-color truncate">
                  {{ riderNameDisplay || 'Pas encore assigné' }}
                </div>
                <div
                  class="flex items-center gap-2 mt-0.5"
                  v-if="riderNameDisplay"
                >
                  <div class="flex items-center gap-0.5 text-yellow-500">
                    <span class="text-xs font-bold">{{
                      deliveryData?.rider?.rating || ''
                    }}</span>
                  </div>
                  <span class="text-slate-300 text-xs">•</span>
                  <span class="text-xs text-slate-500 truncate capitalize">
                    {{ deliveryData?.rider?.vehicle || 'Moto' }}
                  </span>
                </div>
              </div>
              <div class="flex gap-2">
                <a
                  v-if="deliveryData?.rider?.phone"
                  :href="`tel:${deliveryData?.rider?.phone}`"
                  class="w-9 h-9 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </a>
                <button
                  @click="openChatWithRider"
                  class="w-9 h-9 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Trajet complet avec checkpoints -->
          <div class="pt-5 pb-5">
            <div class="text-lg font-bold title-color mb-4">Trajet</div>
            <div class="flex flex-col">
              <!-- Départ -->
              <div class="flex items-start gap-3">
                <div class="flex flex-col items-center flex-shrink-0">
                  <div
                    class="w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    :class="
                      hasPassedStep('picked_up')
                        ? 'bg-green-500 text-white'
                        : 'bg-primary'
                    "
                  >
                    <svg
                      v-if="hasPassedStep('picked_up')"
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div
                    class="w-0.5 flex-1 bg-primary/30 my-1 min-h-[1.5rem]"
                  ></div>
                </div>
                <div class="pb-3 flex-1">
                  <div
                    class="text-xs font-bold text-primary uppercase tracking-wider mb-0.5"
                  >
                    Départ
                  </div>
                  <div class="text-sm font-semibold text-gray-800">
                    {{
                      deliveryData?.pickup?.address ||
                      deliveryData?.pickupAddress ||
                      'Départ'
                    }}
                  </div>
                  <div
                    v-if="
                      deliveryData?.pickup?.contactName ||
                      deliveryData?.pickup?.name ||
                      deliveryData?.pickupContactName
                    "
                    class="text-xs text-gray-400 mt-0.5"
                  >
                    {{
                      deliveryData?.pickup?.contactName ||
                      deliveryData?.pickup?.name ||
                      deliveryData?.pickupContactName
                    }}{{
                      deliveryData?.pickup?.phone ||
                      deliveryData?.pickupContactPhone
                        ? ' · ' +
                          (deliveryData?.pickup?.phone ||
                            deliveryData?.pickupContactPhone)
                        : ''
                    }}
                  </div>
                </div>
              </div>

              <!-- Checkpoints intermédiaires -->
              <template v-if="deliveryData?.checkpoints?.length">
                <div
                  v-for="(cp, index) in deliveryData.checkpoints"
                  :key="'cp-' + index"
                  class="flex items-start gap-3"
                >
                  <div class="flex flex-col items-center flex-shrink-0">
                    <div
                      class="w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                      :class="
                        cp.isReached || cp.completed
                          ? 'bg-green-500 text-white'
                          : 'bg-orange-400 text-white'
                      "
                    >
                      <svg
                        v-if="cp.isReached || cp.completed"
                        class="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span v-else class="text-[10px] font-bold">{{
                        index + 1
                      }}</span>
                    </div>
                    <div
                      class="w-0.5 flex-1 bg-primary/30 my-1 min-h-[1.5rem]"
                    ></div>
                  </div>
                  <div class="pb-3 flex-1">
                    <div
                      class="text-xs font-bold text-orange-500 uppercase tracking-wider mb-0.5"
                    >
                      Checkpoint {{ index + 1 }}
                      {{ cp.isDelivered ? '(Atteint)' : '' }}
                    </div>
                    <div class="text-sm font-semibold text-gray-800">
                      {{ cp.address || cp.label || `Point ${index + 1}` }}
                    </div>
                    <div
                      v-if="cp.contactName"
                      class="text-xs text-gray-400 mt-0.5"
                    >
                      {{ cp.contactName }}{{ cp.phone ? ' · ' + cp.phone : '' }}
                    </div>

                    <!-- Code inline du checkpoint -->
                    <button
                      v-if="getCheckpointCode(cp, index)"
                      @click.stop="copyCheckpointCode(cp, index)"
                      class="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-orange-50 border border-orange-100 hover:bg-orange-100 transition-colors"
                      :title="`Copier le code du checkpoint ${index + 1}`"
                    >
                      <span
                        class="text-[10px] font-bold uppercase text-orange-600"
                        >Code</span
                      >
                      <span
                        class="text-sm font-bold tracking-[0.2em] text-orange-700"
                        >{{ getCheckpointCode(cp, index) }}</span
                      >
                      <svg
                        class="w-3.5 h-3.5 text-orange-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </template>

              <!-- Destination -->
              <div class="flex items-start gap-3">
                <div class="flex flex-col items-center flex-shrink-0">
                  <div
                    class="w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    :class="
                      hasPassedStep('delivered')
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300'
                    "
                  >
                    <svg
                      v-if="hasPassedStep('delivered')"
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div class="flex-1">
                  <div
                    class="text-xs font-bold text-green-600 uppercase tracking-wider mb-0.5"
                  >
                    Destination
                  </div>
                  <div class="text-sm font-semibold text-gray-800">
                    {{
                      deliveryData?.destination?.address ||
                      deliveryData?.destinationAddress ||
                      'Destination'
                    }}
                  </div>
                  <div
                    v-if="
                      deliveryData?.destination?.contactName ||
                      deliveryData?.destination?.name ||
                      deliveryData?.destinationContactName
                    "
                    class="text-xs text-gray-400 mt-0.5"
                  >
                    {{
                      deliveryData?.destination?.contactName ||
                      deliveryData?.destination?.name ||
                      deliveryData?.destinationContactName
                    }}{{
                      deliveryData?.destination?.phone ||
                      deliveryData?.destinationContactPhone
                        ? ' · ' +
                          (deliveryData?.destination?.phone ||
                            deliveryData?.destinationContactPhone)
                        : ''
                    }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Distance / durée -->
            <div class="flex gap-4 mt-4 text-sm text-gray-500">
              <span v-if="deliveryData?.distanceKm"
                >📍 {{ deliveryData.distanceKm }} km</span
              >
              <span
                v-if="deliveryData?.durationMin && deliveryData.durationMin > 0"
                >⏱ {{ deliveryData.durationMin }} min</span
              >
              <span v-if="deliveryData?.checkpoints?.length"
                >🏁 {{ deliveryData.checkpoints.length }} arrêt{{
                  deliveryData.checkpoints.length > 1 ? 's' : ''
                }}</span
              >
            </div>
          </div>

          <!-- Timer Card -->
          <div
            v-if="
              deliveryData?.status &&
              ![
                'arrived',
                'delivered',
                'completed',
                'cancelled',
                'canceled',
              ].includes(deliveryData.status)
            "
            class="mb-4"
          >
            <div
              class="bg-primary/10 rounded-2xl p-4 flex items-center justify-between"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-full bg-primary flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <div class="text-sm font-bold text-slate-800">
                    Livraison dans
                  </div>
                  <div class="text-xs text-slate-500">Course & Livraison</div>
                </div>
              </div>
              <div
                class="text-xl font-black text-primary min-w-[80px] text-right"
              >
                <template v-if="!isPackageInProgress">
                  <span class="text-sm font-bold text-slate-400">
                    En attente
                    <span
                      v-if="deliveryData?.durationMin"
                      class="font-normal opacity-70"
                      >({{ deliveryData.durationMin }} min)</span
                    >
                  </span>
                </template>
                <template v-else-if="timerExpired">
                  <span class="text-base font-bold">Bientôt livré</span>
                </template>
                <template v-else>
                  {{ remainingMinutes }}
                  <span class="text-sm font-bold">min</span>
                  {{ remainingSeconds.toString().padStart(2, '0') }}
                  <span class="text-sm font-bold">s</span>
                </template>
              </div>
            </div>
          </div>

          <!-- Détails du colis -->
          <div class="pt-5 border-t">
            <div class="text-lg font-bold title-color mb-3">
              Détails du colis
            </div>
            <div class="text-sm text-gray-600 space-y-2">
              <div v-if="packageCategory === 'document'">
                Nombre de documents :
                <b>{{ documentQuantity }}</b>
              </div>
              <template v-else>
                <div>Catégorie : <b>{{ packageCategoryDisplay }}</b></div>
                <div
                  v-if="
                    deliveryData?.packageInfo?.weight?.value ||
                    deliveryData?.packageType?.parcelDetails?.weight?.value
                  "
                >
                  Poids :
                  <b>
                    {{
                      deliveryData?.packageInfo?.weight?.value ||
                      deliveryData?.packageType?.parcelDetails?.weight?.value
                    }}
                    {{
                      deliveryData?.packageInfo?.weight?.unit ||
                      deliveryData?.packageType?.parcelDetails?.weight?.unit ||
                      'kg'
                    }}
                  </b>
                </div>
                <div
                  v-if="
                    deliveryData?.packageInfo?.dimensions?.length ||
                    deliveryData?.packageType?.parcelDetails?.length
                  "
                >
                  Dimensions :
                  <b>
                    {{
                      deliveryData?.packageInfo?.dimensions?.length ||
                      deliveryData?.packageType?.parcelDetails?.length
                    }}
                    ×
                    {{
                      deliveryData?.packageInfo?.dimensions?.width ||
                      deliveryData?.packageType?.parcelDetails?.width
                    }}
                    ×
                    {{
                      deliveryData?.packageInfo?.dimensions?.height ||
                      deliveryData?.packageType?.parcelDetails?.height
                    }}
                    {{ deliveryData?.packageInfo?.dimensions?.unit || 'cm' }}
                  </b>
                </div>
                <div
                  v-if="
                    deliveryData?.packageInfo?.fragile ||
                    deliveryData?.packageType?.parcelDetails?.fragile
                  "
                >
                  <span class="text-orange-500 font-semibold">⚠️ Fragile</span>
                </div>
              </template>
              <div
                v-if="deliveryData?.packageInfo?.description"
                class="text-gray-500 text-xs"
              >
                {{ deliveryData.packageInfo.description }}
              </div>
            </div>
          </div>

          <!-- Code de confirmation (destination finale) + codes checkpoints -->
          <div class="pt-5 mt-5 border-t">
            <div class="text-sm font-semibold mb-2 title-color">
              Code de confirmation (destination finale)
            </div>
            <div class="text-xs text-red-500 mb-2">
              Ne donnez ce code qu'après réception du colis
            </div>
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="copyDeliveryCode"
            >
              <span class="text-2xl font-bold text-primary tracking-[0.3em]">
                {{ baseDeliveryCodeDisplay || 'N/A' }}
              </span>
              <div class="w-6 h-6">
                <ColorImg
                  class="bg-primary w-full h-full"
                  src="/images/all/copy.svg"
                />
              </div>
            </div>

            <!-- Codes des checkpoints -->
            <div
              v-if="checkpointCodesList.length"
              class="mt-4 pt-4 border-t border-dashed"
            >
              <div class="text-sm font-semibold mb-2 title-color">
                Codes de confirmation des checkpoints
              </div>
              <div class="text-xs text-gray-500 mb-3">
                Chaque checkpoint possède son propre code, dérivé du code de
                base. Le livreur utilisera ce même code au moment de la remise.
              </div>
              <div class="space-y-2">
                <div
                  v-for="entry in checkpointCodesList"
                  :key="'code-' + entry.index"
                  class="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-orange-50 border border-orange-100"
                >
                  <div class="min-w-0 flex-1">
                    <div
                      class="text-[10px] font-bold uppercase tracking-wider text-orange-600"
                    >
                      Checkpoint {{ entry.index + 1 }}
                      <span
                        v-if="
                          entry.checkpoint?.isReached ||
                          entry.checkpoint?.completed
                        "
                        class="text-green-600"
                        >• atteint</span
                      >
                    </div>
                    <div class="text-xs text-gray-600 truncate">
                      {{
                        entry.checkpoint?.address ||
                        entry.checkpoint?.label ||
                        `Point ${entry.index + 1}`
                      }}
                    </div>
                  </div>
                  <button
                    @click.stop="
                      copyCheckpointCode(entry.checkpoint, entry.index)
                    "
                    class="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-orange-200 hover:bg-orange-100 transition-colors"
                  >
                    <span
                      class="text-sm font-bold tracking-[0.2em] text-orange-700"
                      >{{ entry.code || '—' }}</span
                    >
                    <svg
                      class="w-3.5 h-3.5 text-orange-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Paiement -->
          <div class="pt-5 mt-5 border-t">
            <div class="text-sm font-semibold mb-3 title-color">Paiement</div>
            <div class="space-y-2 text-sm">
              <div class="flex items-center justify-between">
                <span class="text-gray-500">Méthode</span>
                <span class="font-semibold text-gray-800 capitalize">
                  {{
                    deliveryData?.payment?.method === 'mobile_money'
                      ? 'Mobile Money'
                      : deliveryData?.payment?.method === 'cash'
                        ? 'Espèces'
                        : deliveryData?.payment?.method === 'wallet'
                          ? 'Portefeuille'
                          : deliveryData?.payment?.method || '—'
                  }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-gray-500">Statut</span>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-bold uppercase"
                  :class="
                    deliveryData?.payment?.status === 'completed'
                      ? 'bg-emerald-100 text-emerald-700'
                      : deliveryData?.payment?.status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-orange-100 text-orange-700'
                  "
                >
                  {{
                    deliveryData?.payment?.status === 'completed'
                      ? 'Payé'
                      : deliveryData?.payment?.status === 'failed'
                        ? 'Échec'
                        : 'En attente'
                  }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-gray-500">Frais de livraison</span>
                <span class="font-semibold text-gray-800">
                  {{
                    formatPrice(
                      deliveryData?.pricing?.deliveryFee ||
                        deliveryData?.pricing?.originalDeliveryFee ||
                        deliveryData?._raw?.pricing?.deliveryFee ||
                        0
                    )
                  }}
                </span>
              </div>
              <div
                v-if="
                  (deliveryData?.pricing?.serviceFee ||
                    deliveryData?._raw?.pricing?.serviceFee ||
                    0) > 0
                "
                class="flex items-center justify-between"
              >
                <span class="text-gray-500">Frais de service</span>
                <span class="font-semibold text-gray-800">
                  {{
                    formatPrice(
                      deliveryData?.pricing?.serviceFee ||
                        deliveryData?._raw?.pricing?.serviceFee ||
                        0
                    )
                  }}
                </span>
              </div>
              <div
                v-if="(deliveryData?.metadata?.merchandisePrice || 0) > 0"
                class="flex items-center justify-between"
              >
                <span class="text-gray-500">Valeur marchandise</span>
                <span class="font-semibold text-gray-800">
                  {{ formatPrice(deliveryData.metadata.merchandisePrice) }}
                </span>
              </div>
              <div
                v-if="deliveryData?.metadata?.deliveryFeePayer"
                class="flex items-center justify-between"
              >
                <span class="text-gray-500">Frais livraison à la charge</span>
                <span class="font-semibold text-gray-800">
                  {{
                    deliveryData.metadata.deliveryFeePayer === 'receiver'
                      ? 'Du destinataire'
                      : "De l'expéditeur"
                  }}
                </span>
              </div>
              <div
                class="flex items-center justify-between pt-2 border-t border-gray-100"
              >
                <span class="text-gray-700 font-semibold">Total</span>
                <span class="font-bold text-primary">
                  {{
                    formatPrice(
                      deliveryData?.pricing?.total ||
                        deliveryData?._raw?.pricing?.total ||
                        0
                    )
                  }}
                </span>
              </div>
            </div>
            <div
              v-if="
                deliveryData?.status !== 'delivered' &&
                deliveryData?.status !== 'cancelled' &&
                deliveryData?.payment?.status !== 'completed' &&
                deliveryData?.payment?.method !== 'cash' &&
                deliveryData?.metadata?.paymentLinkEnabled
              "
              class="mt-4"
            >
              <div class="text-xs text-gray-500 mb-2">
                Partagez ce lien pour permettre le paiement.
              </div>
              <button
                @click="copyPaymentLink"
                class="w-full flex items-center justify-center gap-2 px-3 py-3 bg-orange-100 text-orange-700 rounded-lg text-sm font-bold hover:bg-orange-200 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Copier le lien de paiement
              </button>
            </div>
          </div>

          <!-- Partager le suivi -->
          <div class="pt-5 mt-5 border-t">
            <div class="text-sm font-semibold mb-2 title-color">
              Partager le suivi
            </div>
            <div class="text-xs text-gray-500 mb-3">
              Partagez ce lien avec le destinataire pour qu'il puisse suivre la
              livraison en temps réel
            </div>
            <div class="flex gap-2">
              <button
                @click="copyTrackingLink"
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Copier le lien
              </button>
              <button
                @click="copyTrackingCode"
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 title-color rounded-lg text-sm hover:bg-gray-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Copier le code
              </button>
            </div>
          </div>

          <!-- Timeline des étapes -->
          <div class="pt-5 mt-5 border-t">
            <div class="text-lg font-bold title-color mb-3">Historique</div>
            <div class="flex flex-col gap-3">
              <div
                v-for="step in timelineSteps"
                :key="step.key"
                class="flex items-center gap-3"
                :class="{ 'opacity-40': !step.completed }"
              >
                <div
                  class="rounded-full w-4 h-4 flex-shrink-0"
                  :class="step.completed ? 'bg-primary' : 'bg-gray-300'"
                ></div>
                <div class="flex-1">
                  <div
                    class="text-sm font-semibold"
                    :class="step.completed ? 'title-color' : 'text-gray-400'"
                  >
                    {{ step.label }}
                  </div>
                  <div v-if="step.date" class="text-xs text-gray-500">
                    {{ step.date }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Map from '@features/map/Map.vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Commons from '@features/shared/libraries/commons';

const props = defineProps({
  deliveryData: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const mapRef = ref(null);
const detailContainer = ref(null);
const onceLaunched = ref(false);


function resolveCheckpointCode(delivery, checkpoint) {
  const code = checkpoint?.deliveryCode || delivery?.deliveryCode
  if (!code) return ''

  const base = `${code}`.trim().toUpperCase().split('-').pop().substring(0, 5)
  if (!base) return ''

  // position = XOR de (lon × 1e6) et (lat × 1e6) → entier déterministe
  const pos = Math.abs(
    ((Number(checkpoint?.longitude) || 0) * 1e6) ^
    ((Number(checkpoint?.latitude)  || 0) * 1e6)
  ) || 1

  const D = '0123456789', L = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let out = ''
  for (let i = 0; i < base.length; i++) {
    const c = base[i]
    const s = (pos * 7 + (i + 1) * 3) % 10
    const d = D.indexOf(c)
    const l = L.indexOf(c)
    if (d !== -1)      out += D[(d + s) % 10]
    else if (l !== -1) out += L[(l + s) % 26]
    else               out += c
  }
  return out
}

/* === FIN FONCTION CODE CHECKPOINT === */

/* ------------------------------------------------------------------ */
/*  CODES — utilisation dans le composant                             */
/* ------------------------------------------------------------------ */

const baseDeliveryCodeDisplay = computed(() => {
  const raw =
    props.deliveryData?.deliveryCode
  if (!raw) return ''
  let c = `${raw}`.trim().toUpperCase()
  if (c.includes('-')) c = c.split('-').pop()
  return c.substring(0, 5)
})

const checkpointCodesList = computed(() => {
  const cps = props.deliveryData?.checkpoints || []
  return cps.map((cp) => ({
    checkpoint: cp,
    code: resolveCheckpointCode(props.deliveryData, cp),
  }))
})

function getCheckpointCode(cp) {
  return resolveCheckpointCode(props.deliveryData, cp)
}

function copyCheckpointCode(cp, index) {
  const code = getCheckpointCode(cp)
  if (!code) return
  Commons.copyToClipboard(
    code,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Code copié',
        body: `Code du checkpoint ${index + 1} copié`,
      })
    },
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur',
        body: 'Impossible de copier le code',
      })
    }
  )
}

/* ------------------------------------------------------------------ */
/*  TIMER                                                              */
/* ------------------------------------------------------------------ */

const remainingMinutes = ref(0);
const remainingSeconds = ref(0);
const timerExpired = ref(false);
let timerInterval = null;

const isPackageInProgress = computed(() => {
  const status = props.deliveryData?.status;
  return [
    'assigned',
    'accepted',
    'picked_up',
    'in_transit',
    'inTransit',
    'arrived',
  ].includes(status);
});

const riderNameDisplay = computed(() => {
  const rider = props.deliveryData?.rider || props.deliveryData?.delivery?.rider;
  if (!rider) return null;
  if (rider.name) return rider.name;
  if (rider.firstName || rider.lastName) {
    return `${rider.firstName || ''} ${rider.lastName || ''}`.trim();
  }
  return null;
});

const documentQuantity = computed(() => {
  return (
    props.deliveryData?.packageInfo?.quantity ||
    props.deliveryData?.packageType?.parcelDetails?.quantity ||
    1
  );
});

const getAssignedAt = () => {
  const timeline =
    props.deliveryData?.timeline || props.deliveryData?._raw?.timeline || [];
  const entry = timeline.find(
    (t) =>
      t.status === 'assigned' ||
      t.status === 'accepted' ||
      t.status === 'assigned_rider'
  );
  return (
    entry?.timestamp ||
    props.deliveryData?.assignedAt ||
    props.deliveryData?.delivery?.assignedAt ||
    props.deliveryData?.confirmedAt ||
    props.deliveryData?.createdAt ||
    null
  );
};

const updateTimer = () => {
  if (!props.deliveryData) return;

  const totalMin = props.deliveryData.durationMin || 0;
  if (!totalMin || !isPackageInProgress.value) {
    remainingMinutes.value = 0;
    remainingSeconds.value = 0;
    timerExpired.value = false;
    return;
  }

  const assignedAt = getAssignedAt();
  const startMs = assignedAt
    ? new Date(assignedAt).getTime()
    : props.deliveryData.createdAt
      ? new Date(props.deliveryData.createdAt).getTime()
      : Date.now();
  const targetTimeMs = startMs + totalMin * 60 * 1000;
  const diffMs = targetTimeMs - Date.now();

  if (diffMs <= 0) {
    timerExpired.value = true;
    remainingMinutes.value = 0;
    remainingSeconds.value = 0;
    return;
  }

  timerExpired.value = false;
  remainingMinutes.value = Math.floor(diffMs / 60000);
  remainingSeconds.value = Math.floor((diffMs % 60000) / 1000);
};

watch(
  () => props.deliveryData,
  () => {
    updateTimer();
  },
  { deep: true }
);

onMounted(() => {
  updateTimer();
  timerInterval = setInterval(updateTimer, 1000);

  setTimeout(() => {
    if (onceLaunched.value) return;
    traceRoute();
  }, 500);
});

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
});

const etaMessage = computed(() => {
  const durationRaw =
    props.deliveryData?.durationRemaining ||
    props.deliveryData?.route?.durationRemaining ||
    props.deliveryData?.estimatedDeliveryTime;

  let duration = null;

  if (typeof durationRaw === 'number') {
    duration = durationRaw;
  } else if (typeof durationRaw === 'string') {
    const match = durationRaw.match(/(\d+)/);
    if (match) duration = parseInt(match[1]);
  }

  if (duration === null) return null;

  if (duration <= 2) {
    return {
      title: 'RIDER EST LÀ, SORTEZ PRENDRE VOTRE COLIS',
      subtitle: "Votre livreur vient d'arriver à votre destination",
    };
  } else {
    return {
      title: `RIDER ARRIVE DANS ${duration} MIN, APPRÊTEZ-VOUS À RECEVOIR VOTRE COLIS`,
      subtitle: 'Suivez son avancée en temps réel sur la carte ci-dessus',
    };
  }
});

const packageCategory = computed(() => {
  return (
    props.deliveryData?.packageInfo?.category ||
    props.deliveryData?.packageType?.type ||
    props.deliveryData?.packageType?.parcelDetails?.category ||
    'parcel'
  );
});

const packageCategoryDisplay = computed(() => {
  const catMap = {
    document: 'Document',
    parcel: 'Colis',
    electronics: 'Électronique',
    food: 'Alimentation',
    fragile: 'Fragile',
    medicine: 'Médicament',
  };
  return catMap[packageCategory.value] || packageCategory.value || 'Colis';
});

const pickupMethodDisplay = computed(() => {
  const method =
    props.deliveryData?.deliveryType?.deliveryType ||
    props.deliveryData?.deliveryType ||
    props.deliveryData?.packageInfo?.pickupMethod ||
    props.deliveryData?.packageType?.parcelDetails?.pickupMethod ||
    '';
  const map = {
    car: 'Voiture',
    moto: 'Moto',
    motorcycle: 'Moto',
    scheduled_pickup: 'Enlèvement programmé',
    on_demand: 'À la demande',
  };
  return map[method] || 'Moto';
});

const statusDisplay = computed(() => {
  const status = props.deliveryData?.status || 'created';
  const statusMap = {
    created: 'Créée',
    confirmed: 'Confirmée',
    assigned: 'Acceptée',
    picked_up: 'Colis récupéré',
    in_transit: 'En transit',
    inTransit: 'En transit',
    at_checkpoint: 'Au point de passage',
    delivered: 'Livrée',
    cancelled: 'Annulée',
    canceled: 'Annulée',
  };
  return statusMap[status] || 'En cours';
});

const progressWidth = computed(() => {
  const status = props.deliveryData?.status || '';
  const progressMap = {
    created: '5%',
    confirmed: '7%',
    assigned: '10%',
    picked_up: '30%',
    in_transit: '40%',
    inTransit: '40%',
    at_checkpoint: '60%',
    delivered: '100%',
    cancelled: '0%',
    canceled: '0%',
  };
  return progressMap[status] || '10%';
});

const timelineSteps = computed(() => {
  const rawTimeline = props.deliveryData?.timeline || [];

  const getTs = (status) => {
    const entry = rawTimeline.find((t) => t.status === status);
    return entry ? entry.timestamp : null;
  };

  const steps = [
    {
      key: 'created',
      label: 'Colis enregistré',
      ts: getTs('created') || props.deliveryData?.createdAt,
    },
    {
      key: 'confirmed',
      label: 'Livraison confirmée',
      ts: getTs('confirmed'),
    },
    {
      key: 'assigned',
      label: 'Livraison acceptée',
      ts: getTs('assigned'),
    },
    {
      key: 'picked_up',
      label: 'Colis récupéré',
      ts: getTs('picked_up') || props.deliveryData?.pickedUpAt,
    },
    {
      key: 'in_transit',
      label: 'En route',
      ts:
        getTs('in_transit') ||
        getTs('inTransit') ||
        props.deliveryData?.inTransitAt,
    },
    {
      key: 'delivered',
      label: 'Livré',
      ts: getTs('delivered') || props.deliveryData?.deliveredAt,
    },
  ];

  const currentStatus = props.deliveryData?.status || '';
  const statusOrder = [
    'created',
    'confirmed',
    'assigned',
    'picked_up',
    'in_transit',
    'inTransit',
    'at_checkpoint',
    'delivered',
  ];
  const currentIndex = statusOrder.indexOf(currentStatus);

  return steps.map((step, i) => ({
    ...step,
    date: formatDate(step.ts),
    completed: !!step.ts || (currentIndex !== -1 && currentIndex >= i),
  }));
});

function hasPassedStep(stepKey) {
  const currentStatus = props.deliveryData?.status || '';
  const statusOrder = [
    'created',
    'confirmed',
    'assigned',
    'picked_up',
    'in_transit',
    'inTransit',
    'at_checkpoint',
    'delivered',
  ];

  const currentIndex = statusOrder.indexOf(currentStatus);
  const stepIndex = statusOrder.indexOf(stepKey);

  if (currentStatus === 'delivered') return true;
  if (currentIndex === -1 || stepIndex === -1) return false;

  return currentIndex >= stepIndex;
}

function traceRouteWithDelay() {
  setTimeout(traceRoute, 100);
}

function traceRoute() {
  onceLaunched.value = true;
  if (!mapRef.value || !props.deliveryData) return;

  const d = props.deliveryData;
  const pickupLat =
    parseFloat(
      d?.pickup?.latitude || d?.pickup?.coordinates?.lat || d?.pickupLat
    ) || 6.3571;
  const pickupLng =
    parseFloat(
      d?.pickup?.longitude || d?.pickup?.coordinates?.lng || d?.pickupLng
    ) || 2.3699;
  const destinationLat =
    parseFloat(
      d?.destination?.latitude ||
        d?.destination?.coordinates?.lat ||
        d?.destinationLat
    ) || 6.3832;
  const destinationLng =
    parseFloat(
      d?.destination?.longitude ||
        d?.destination?.coordinates?.lng ||
        d?.destinationLng
    ) || 2.4156;

  const checkpoints = (d?.checkpoints || [])
    .map((cp, i) => ({
      lat: parseFloat(cp.lat || cp.latitude || cp.coordinates?.lat) || 6.3701,
      lng: parseFloat(cp.lng || cp.longitude || cp.coordinates?.lng) || 2.3923,
      content: cp.label || cp.address || `Point de passage ${i + 1}`,
      iconType: 'checkpoint',
    }))
    .filter((cp) => cp.lat && cp.lng);

  mapRef.value.traceRouteWithPoints(
    'delivery-route',
    {
      lat: pickupLat,
      lng: pickupLng,
      content:
        d?.pickup?.address || d?.pickupLabel || d?.pickupAddress || 'Départ',
      iconType: 'pickup',
    },
    {
      lat: destinationLat,
      lng: destinationLng,
      content:
        d?.destination?.address ||
        d?.destinationLabel ||
        d?.destinationAddress ||
        'Destination',
      iconType: 'destination',
    },
    checkpoints
  );
}

function formatPrice(amount) {
  if (!amount) return '0 FCFA';
  return (
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
    }).format(amount) + ' FCFA'
  );
}

function formatDate(timestamp) {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

function copyDeliveryCode() {
  const code = baseDeliveryCodeDisplay.value;
  if (!code) return;
  Commons.copyToClipboard(
    code,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Copié',
        body: 'Code copié dans le presse-papier',
      });
    },
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur',
        body: 'Impossible de copier le code',
      });
    }
  );
}

function copyTrackingLink() {
  const code =
    props.deliveryData?.trackingCode || props.deliveryData?.deliveryCode;
  if (!code) return;
  const baseUrl =
    window.$constants?.URLS?.packageTrackingBaseUrl || window.location.origin;
  const link = `${baseUrl}/track/package/${code}`;
  Commons.copyToClipboard(
    link,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Lien copié',
        body: 'Lien de tracking copié dans le presse-papier',
      });
    },
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur',
        body: 'Impossible de copier le lien',
      });
    }
  );
}

function copyTrackingCode() {
  const code =
    props.deliveryData?.trackingCode || props.deliveryData?.deliveryCode;
  if (!code) return;
  Commons.copyToClipboard(
    code,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Code copié',
        body: 'Code de tracking copié dans le presse-papier',
      });
    },
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur',
        body: 'Impossible de copier le code',
      });
    }
  );
}

function copyPaymentLink() {
  const id = props.deliveryData?.id;
  if (!id) return;
  const baseUrl =
    window.$constants?.URLS?.packagePaymentBaseUrl || window.location.origin;
  const link = `${baseUrl}/pay/package/${id}`;
  Commons.copyToClipboard(
    link,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Lien de paiement copié',
        body: 'Le lien de paiement a été copié dans le presse-papier',
      });
    },
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur',
        body: 'Impossible de copier le lien de paiement',
      });
    }
  );
}

function closeDialog() {
  emit('close');
}

function openChatWithRider() {
  if (!riderNameDisplay.value) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Rider non assigné',
      body: "Aucun rider n'a été assigné à votre commande pour le moment",
    });
    return;
  }
  bus.emit('chat.open_with_rider', props.deliveryData);
}

defineExpose({
  traceRoute,
  traceRouteWithDelay,
  getCheckpointCode,
  resolveCheckpointCode,
});
</script>